/*!
 * controlAccess.js — who may operate a control
 * © 2025 Rob Canning — GPLv3
 *
 *     ui(uid:spd1, step:0.05, click:1, allow:[director])
 *     ui(uid:spd1, step:0.05, click:1, allow:[director], lock:"plxLock.t>0.5")
 *     o2p(path:…, trig:touch, uid:spd1, allow:[director], lock:"plxLock.t>0.5")
 *
 * allow: names the clients (their ?role= / client name) that may operate the
 * control; anyone else's press does nothing. With lock: the restriction holds
 * only while that condition (the if: form, read from the bus) is true, so a
 * director can open and close the score to everyone during the piece.
 *
 * Checked where a person operates something: click:1 overlays (button.js),
 * touch-fader drags (o2pTouch.js) and hit-label clicks, which start/pause
 * armed motions and fire o2p target buttons (animOverlays.js, rotate.js).
 * A cue fired by the score, by OSC or by another client is not a person
 * pressing here, and is not checked.
 */

import { parseCueToAST } from "../parser/parser.js";
import { splitCueId } from "../parser/cueUtils.js";
import { parseCondition, isCondition } from "../parser/parserSignalRef.js";
import { evaluateCondition } from "./cueDispatcher.js";

const argOf = (ast, key) => ast?.args?.find(a => a?.type === key)?.value ?? ast?.params?.[key];

/** allow/lock from an AST, or from every cue an element carries */
function accessOf(source) {
  if (!source) return {};
  if (!(source instanceof Element)) return { allow: argOf(source, "allow"), lock: argOf(source, "lock") };
  const dsl = source.getAttribute("data-oscilla") || source.id || "";
  if (source._oscillaAccess?.dsl === dsl) return source._oscillaAccess.out;
  const out = {};
  for (const expr of splitCueId(dsl.trim())) {
    let ast;
    try { ast = parseCueToAST(expr); } catch { continue; }
    out.allow ??= argOf(ast, "allow");
    out.lock ??= argOf(ast, "lock");
  }
  source._oscillaAccess = { dsl, out };      // parsed once per edit of the cue
  return out;
}

function lockEngaged(lock) {
  if (lock == null) return true;              // allow: alone always applies
  const cond = isCondition(lock) ? lock : parseCondition(String(lock).replace(/^["'`]+|["'`]+$/g, ""));
  return cond ? evaluateCondition(cond) : true;
}

/**
 * May this client operate the control? `source` is the cue's AST or the
 * element carrying it. Says why not, briefly, when it may not.
 */
export function mayOperate(source) {
  const { allow, lock } = accessOf(source);
  if (allow == null) return true;
  const names = (Array.isArray(allow) ? allow : [allow]).map(String);
  if (names.includes(window.localClientName || "")) return true;
  if (!lockEngaged(lock)) return true;
  notice(`🔒 ${names.join(", ")} only`);
  return false;
}

let noticeEl = null, noticeTimer = null;
function notice(text) {
  if (!noticeEl) {
    noticeEl = document.createElement("div");
    noticeEl.className = "oscilla-access-notice";
    Object.assign(noticeEl.style, {
      position: "fixed", left: "50%", bottom: "12%", transform: "translateX(-50%)",
      background: "rgba(20,20,20,0.88)", color: "#fff", font: "15px sans-serif",
      padding: "8px 14px", borderRadius: "6px", zIndex: "1000000", pointerEvents: "none",
    });
    document.body.appendChild(noticeEl);
  }
  noticeEl.textContent = text;
  noticeEl.style.display = "";
  clearTimeout(noticeTimer);
  noticeTimer = setTimeout(() => { noticeEl.style.display = "none"; }, 1800);
}
